/* eslint-disable no-unused-vars */
import { motion, AnimatePresence } from "framer-motion";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";

const Questionnaire = ({
  currentQuestion,
  setCurrentQuestion,
  questions,
  step,
  setStep,
  answers,
  setAnswers,
  handleNext,
  handleAnswer,
  setQuestions,
}) => {
  const handleBackQuestion = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleBack = () => {
    setStep((prev) => (prev > 1 ? prev - 1 : prev));
    handleBackQuestion();
  };

  const CustomCheckboxRadio = ({
    question,
    type = "checkbox",
    wrapperClass = "",
    labelClass = "",
    name,
    value,
    label,
    id,
    required = false,
  }) => {
    const isSelected = questions[currentQuestion - 1]?.selectedAnswers?.includes(
      value?.toString()
    );

    const handleChange = () => {
      if (type === "checkbox") {
        handleAnswer(question, value, "checkbox");
      } else {
        handleAnswer(question, value, "radio");
      }
    };

    return (
      <label
        className={`taxqa-option d-flex taxqa-${type} ${
          isSelected ? "taxqa-selected" : ""
        } ${wrapperClass}`}
        htmlFor={id}
      >
        <input
          type={type}
          name={name}
          id={id}
          value={value}
          checked={isSelected}
          onChange={handleChange}
          required={required}
          className="taxqa-input-hidden"
        />

        {type === "checkbox" && (
          <span className="taxqa-check-icon">
            {isSelected ? (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="24" height="24" rx="4" fill="#4A4FFF" />
                <path
                  d="M7 12.5L10.5 16L17 9"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="2"
                  y="2"
                  width="20"
                  height="20"
                  rx="4"
                  stroke="#4A4FFF"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="M7 12.5L10.5 16L17 9"
                  stroke="#4A4FFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
        )}

        <span className={`taxqa-option-label fs-5 ${labelClass}`}>{label}</span>
      </label>
    );
  };

  const containerVariant = {
    hidden: { opacity: 0, x: 60, scale: 0.98 },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.08,
        duration: 0,
        ease: [0.1, 1, 0.36, 1],
      },
    },
    exit: {
      opacity: 0,
      x: -60,
      scale: 0.96,
      transition: {
        duration: 0.2,
        ease: "easeInOut",
      },
    },
  };

  const answerVariant = {
    hidden: {
      opacity: 0,
      clipPath: "inset(0 100% 0 0)",
      transition: { duration: 0.4, ease: "easeOut" },
    },
    visible: {
      opacity: 1,
      clipPath: "inset(0 0% 0 0)",
      transition: { duration: 0.5, ease: "easeInOut" },
    },
  };

  const isCurrentCustomSelected =
    questions[currentQuestion - 1]?.selectedAnswers?.includes("custom");

  const isNextDisabled = (() => {
    const selected = questions[currentQuestion - 1]?.selectedAnswers;
    if (!selected || selected.length === 0) return true;
    if (selected.includes("custom")) {
      return !selected[1];
    }
    return false;
  })();

  return (
    <div className="taxqa-card-wrapper">
      <div className="taxqa-card px-4 d-flex justify-content-center border-0">
        <div className="card-body mt-0 d-flex justify-content-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQuestion}
              variants={containerVariant}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="taxqa-inner"
            >
              <motion.p
                variants={answerVariant}
                className="taxqa-question-count text-dark fw-medium"
              >
                Question {currentQuestion} of {questions?.length}
              </motion.p>

              <motion.h2
                variants={answerVariant}
                className="taxqa-question-title"
              >
                {currentQuestion}. {questions[currentQuestion - 1]?.question}
              </motion.h2>

              <motion.p
                variants={answerVariant}
                className="taxqa-question-subtitle fw-medium fs-5"
              >
                {questions[currentQuestion - 1]?.checkMany
                  ? "Select all that are relevant"
                  : "Select one."}
              </motion.p>

              <div className="taxqa-options-group">
                {questions[currentQuestion - 1]?.answers?.map((answer) => (
                  <motion.div
                    key={answer.id}
                    variants={answerVariant}
                    className="taxqa-option-row"
                  >
                    <CustomCheckboxRadio
                      question={questions[currentQuestion - 1]}
                      type={
                        questions[currentQuestion - 1].checkMany
                          ? "checkbox"
                          : "radio"
                      }
                      wrapperClass={
                        questions[currentQuestion - 1].checkMany
                          ? ""
                          : "taxqa-question-background d-flex justify-content-center"
                      }
                      labelClass=""
                      name="answers"
                      state={answers}
                      value={answer.isField ? "custom" : answer.id}
                      label={answer.answer}
                      fn={setAnswers}
                      id={`option-${answer.id}`}
                      required={true}
                    />

                    {answer?.isField && isCurrentCustomSelected ? (
                      <input
                        className="form-control taxqa-custom-input"
                        type="text"
                        placeholder="Enter your answer"
                        value={
                          questions?.[currentQuestion - 1]?.selectedAnswers?.[1] ||
                          ""
                        }
                        onChange={(e) => {
                          setQuestions((questions || []).map((q) => {
                            if (q?.id === questions[currentQuestion - 1]?.id) {
                              const updatedAnswers = [
                                ...(q.selectedAnswers || []),
                              ];
                              updatedAnswers[1] = e.target.value;
                              return {
                                ...q,
                                selectedAnswers: updatedAnswers,
                              };
                            }
                            return q;
                          }));
                        }}
                      />
                    ) : null}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <p
          className="taxqa-skip-link text-decoration-none"
          onClick={() => handleNext(false, true)}
        >
          Skip Question
        </p>

        <div className="border-0  d-flex justify-content-start taxqa-footer w-100">
          {step === 1 ? (
            <button
              disabled={
                !questions[currentQuestion - 1]?.selectedAnswers?.length > 0
              }
              className="taxqa-btn-primary"
              onClick={() => handleNext(false)}
            >
              Continue
              <FiArrowRight className="taxqa-btn-arrow" />
            </button>
          ) : (
            <div className="d-flex justify-content-between w-100 taxqa-footer-btns">
              <button
                className="taxqa-btn-secondary"
                onClick={handleBack}
              >
                <FiArrowLeft className="taxqa-btn-arrow" /> Back
              </button>

              <button
                className="taxqa-btn-primary"
                onClick={() => handleNext(false)}
                disabled={isNextDisabled}
              >
                Next Step
                <FiArrowRight className="taxqa-btn-arrow" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Questionnaire;
